// client/src/components/pages/LexiStar/PlanDispatch/PlanGrid.tsx
//
// The planning grid (LS-09 §5.8): rows are trips; columns asset, trailers, driver, route/lane,
// load, planned start (single date + time), fuel budget (auto from the server's norms, editable
// with a reason), channel and validation status with the warnings inline under the row. Saved
// (PLANNED) trips are read-only rows; drafts are editable until saved.
//
// ST-00198: the driver is picked from the driver register — by default the driver allocated to
// the row's truck at its planned start ("allocated"), another driver ("chosen") or "No driver" —
// and the channel is read-only: the one the driver's register entry gives (preferred channel and
// opt-ins), with the reason when the preferred one cannot be used. Converted to TypeScript.

import { Fragment, useMemo } from 'react';
import { FaTrash } from 'react-icons/fa';
import { ThemedMultiSelect, ThemedSelect } from '../../../../types/commonComponents';
import type { RegisterDriverRow } from '../../../../types/lexistar-trip-drivers';
import { PLANNING_TIME_PRESETS } from '../../../common/pickerLogic';
import { DateTimeField } from '../StatusBoard/atoms';
import { fmtL, fmtDateTime, fmtKm } from '../StatusBoard/format';
import {
  NO_DRIVER,
  channelBlockText,
  channelLabel,
  driverChannel,
  driverOptionLabel,
} from './planLogic';
import type {
  EffectiveDriver,
  PlanAsset,
  PlanLane,
  PlanRoute,
  PlanRow,
  RowValidation,
} from './planTypes';

const th =
  'px-2 py-2.5 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap';
const cell = 'px-2 py-2 align-top';
const input =
  'w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500';
const selectCls = 'w-full h-9 text-sm';

const STATUS_PILL: Record<RowValidation['status'], { label: string; className: string }> = {
  ready: { label: 'Ready', className: 'bg-emerald-50 text-emerald-700' },
  warnings: { label: 'Check', className: 'bg-amber-50 text-amber-800' },
  errors: { label: 'Fix', className: 'bg-red-50 text-red-700' },
};

/** The picker's option that returns a row to its default (the truck's allocated driver). */
const ALLOCATED = '__allocated__';

const NO_VALIDATION: RowValidation = { errors: [], warnings: [], notes: [], status: 'ready' };

/** The picker's value: a chosen (or matched) driver, "No driver", else '' (the default). */
function pickerValue(driver: EffectiveDriver): string {
  if (driver.kind === 'chosen') return driver.driverId;
  if (driver.kind === 'none') return NO_DRIVER;
  return '';
}

/** What the picker shows while the row is on its default. */
function defaultLabel(driver: EffectiveDriver): string {
  switch (driver.kind) {
    case 'allocation':
      return driver.name;
    case 'pending':
      return 'Finding the driver…';
    case 'text':
      return `“${driver.text}”`;
    default:
      return 'Driver...';
  }
}

const SOURCE_TAG: Partial<
  Record<EffectiveDriver['kind'], { label: string; className: string; title: string }>
> = {
  allocation: {
    label: 'allocated',
    className: 'bg-emerald-50 text-emerald-700',
    title: 'The driver allocated to this truck at the planned start',
  },
  chosen: {
    label: 'chosen',
    className: 'bg-primary-50 text-primary-700',
    title: 'Chosen for this trip',
  },
  text: {
    label: 'not in register',
    className: 'bg-amber-50 text-amber-800',
    title: 'This name matches no single driver in the driver register',
  },
};

/** A pasted name while the register is not loaded. */
const UNCHECKED_TAG = {
  label: 'matched on save',
  className: 'bg-slate-100 text-slate-600',
  title: 'The driver register is not loaded here; the name is matched to it when the plan is saved',
};

function DriverSourceTag({ driver }: { driver: EffectiveDriver }) {
  const meta = driver.kind === 'text' && driver.unchecked ? UNCHECKED_TAG : SOURCE_TAG[driver.kind];
  if (!meta) return null;
  const title =
    driver.kind === 'chosen' && driver.matchedFrom
      ? `Matched “${driver.matchedFrom}” in the driver register`
      : meta.title;
  return (
    <span
      className={`mt-1 inline-block px-1.5 py-px rounded text-[11px] font-medium ${meta.className}`}
      title={title}
      data-driver-source={driver.kind}
    >
      {meta.label}
    </span>
  );
}

/** Read-only: the channel the driver's messages go on (from the register). */
function ChannelCell({ driver, label }: { driver: EffectiveDriver; label: string }) {
  const contact = driver.kind === 'chosen' || driver.kind === 'allocation' ? driver.contact : null;
  if (!contact) {
    return (
      <span
        className="text-sm text-slate-400"
        aria-label={`${label} channel`}
        title="The channel comes from the driver's entry in the driver register"
      >
        —
      </span>
    );
  }
  const plan = driverChannel(contact);
  return (
    <span className="block text-sm" aria-label={`${label} channel`}>
      <span className={plan.channel ? 'text-slate-700' : 'text-amber-800'}>
        {plan.channel ? channelLabel(plan.channel) : 'No channel'}
      </span>
      {plan.preferred && plan.preferredBlocked && (
        <span className="block text-xs text-amber-800">
          {channelBlockText(plan.preferred, plan.preferredBlocked)}
        </span>
      )}
    </span>
  );
}

export interface PlanGridProps {
  rows: PlanRow[];
  validation: Map<string, RowValidation>;
  /** Each row's driver (effectiveDriver), by row key. */
  drivers: Map<string, EffectiveDriver>;
  /** The picker's drivers: the active drivers of the register. */
  driverOptions: RegisterDriverRow[];
  selected: Set<string>;
  onToggle: (key: string) => void;
  onToggleAll: (on: boolean) => void;
  onChange: (key: string, patch: Partial<PlanRow>) => void;
  onRemove: (key: string) => void;
  horses: PlanAsset[];
  trailers: PlanAsset[];
  routes: PlanRoute[];
  lanesById: Map<string, PlanLane>;
}

export default function PlanGrid({
  rows,
  validation,
  drivers,
  driverOptions,
  selected,
  onToggle,
  onToggleAll,
  onChange,
  onRemove,
  horses,
  trailers,
  routes,
  lanesById,
}: PlanGridProps) {
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.key));
  const horsesById = useMemo(() => new Map(horses.map((h) => [h.id, h])), [horses]);
  const severalTenants = useMemo(
    () => new Set(driverOptions.map((d) => d.tenant_id)).size > 1,
    [driverOptions]
  );
  const routeLabel = (r: PlanRoute): string => {
    const lane = lanesById.get(r.lane_id);
    const from = r.from_location_name || lane?.from_location_name || lane?.from_name;
    const to = r.to_location_name || lane?.to_location_name || lane?.to_name;
    const name = r.name || (from && to ? `${from} → ${to}` : '');
    return `${r.code} · ${name}${r.status === 'proposed' ? ' (proposed)' : ''}`;
  };
  /**
   * A global admin sees every organisation's drivers: offer the truck's own organisation's (the
   * server accepts only those). Anyone else's register is their own organisation's already.
   */
  const optionsFor = (row: PlanRow): RegisterDriverRow[] => {
    const tenant = horsesById.get(row.asset_id)?.tenant_id;
    return !severalTenants || tenant == null
      ? driverOptions
      : driverOptions.filter((d) => d.tenant_id === tenant);
  };

  return (
    // Where the pickers open as a bottom sheet (phone width or touch, PICKER_SHEET_QUERY) the
    // scroller holds the cells' absolutely placed sr-only labels; left out, they widened the page
    // to the grid's width, and Chrome on Android then put the sheet below the visible screen
    // (ST-00264). Not on a desktop: the planned-start calendar pops up from inside the grid there.
    <div className="overflow-x-auto max-sm:relative [@media(pointer:coarse)]:relative">
      <table className="min-w-full divide-y divide-slate-200" aria-label="Planning grid">
        <thead className="bg-slate-50">
          <tr>
            <th className="px-2 py-2.5 w-8">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={() => onToggleAll(!allSelected)}
                aria-label="Select all trips"
                className="rounded border-slate-300 text-primary-600 focus:ring-primary-500"
              />
            </th>
            <th className={th}>Asset</th>
            <th className={th}>Trailers</th>
            <th className={th}>Driver</th>
            <th className={th}>Route / lane</th>
            <th className={th}>Load</th>
            <th className={th}>Planned start</th>
            <th className={th}>Fuel budget</th>
            <th className={th}>Channel</th>
            <th className={th}>Validation</th>
            <th className="px-2 py-2.5" />
          </tr>
        </thead>
        <tbody className="bg-surface divide-y divide-slate-200">
          {rows.map((r, i) => {
            const v = validation.get(r.key) || NO_VALIDATION;
            const pill = STATUS_PILL[v.status];
            const driver: EffectiveDriver = drivers.get(r.key) || { kind: 'unresolved' };
            const set = (patch: Partial<PlanRow>) => onChange(r.key, patch);
            const pickDriver = (value: string) =>
              set({
                driver_choice: value === ALLOCATED ? '' : value,
                driver_text: '',
              });
            const label = r.reference || `Draft ${i + 1}`;
            return (
              <Fragment key={r.key}>
                <tr aria-label={label} className={r.saved ? 'bg-slate-50/50' : ''}>
                  <td className={cell}>
                    <input
                      type="checkbox"
                      checked={selected.has(r.key)}
                      onChange={() => onToggle(r.key)}
                      aria-label={`Select ${label}`}
                      className="mt-2 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                    />
                  </td>
                  {r.saved ? (
                    <>
                      <td className={cell}>
                        <span className="block text-sm font-mono font-medium text-slate-900">
                          {r.registration}
                        </span>
                        <span className="block text-xs font-mono text-slate-500">
                          {r.reference}
                        </span>
                      </td>
                      <td className={`${cell} text-sm font-mono text-slate-700`}>
                        {(r.trailers || []).join(' / ') || '—'}
                      </td>
                      <td className={`${cell} text-sm text-slate-700`}>
                        <span className="block">{r.driver_name || r.driver_tag || '—'}</span>
                        <DriverSourceTag driver={driver} />
                      </td>
                      <td className={`${cell} text-sm text-slate-700 whitespace-nowrap`}>
                        {r.from_name} → {r.to_name}
                        {r.planned_km != null && (
                          <span className="block text-xs text-slate-500">
                            {fmtKm(r.planned_km)}
                          </span>
                        )}
                      </td>
                      <td className={`${cell} text-sm text-slate-700`}>
                        {[
                          r.material,
                          r.order_ref,
                          r.planned_tonnes !== '' ? `${r.planned_tonnes} t` : null,
                        ]
                          .filter(Boolean)
                          .join(' · ') || '—'}
                      </td>
                      <td className={`${cell} text-sm text-slate-700 whitespace-nowrap`}>
                        {fmtDateTime(r.planned_start)}
                      </td>
                      <td className={`${cell} text-sm tabular-nums`}>{fmtL(r.budget_l)}</td>
                      <td className={cell}>
                        <ChannelCell driver={driver} label={label} />
                      </td>
                    </>
                  ) : (
                    <>
                      <td className={`${cell} min-w-[150px]`}>
                        <ThemedSelect
                          value={r.asset_id}
                          onChange={(e) => set({ asset_id: String(e.target.value) })}
                          className={selectCls}
                          aria-label={`${label} asset`}
                        >
                          <option value="">Asset...</option>
                          {horses.map((a) => (
                            <option key={a.id} value={a.id}>
                              {a.registration}
                              {a.fleet_no ? ` (${a.fleet_no})` : ''}
                            </option>
                          ))}
                        </ThemedSelect>
                      </td>
                      <td className={`${cell} min-w-[160px]`}>
                        <ThemedMultiSelect
                          isMulti
                          value={r.trailer_ids}
                          onChange={(e) => set({ trailer_ids: e.target.value })}
                          className={selectCls}
                          aria-label={`${label} trailers`}
                        >
                          <option value="">Trailers...</option>
                          {trailers.map((a) => (
                            <option key={a.id} value={a.id}>
                              {a.registration}
                            </option>
                          ))}
                        </ThemedMultiSelect>
                      </td>
                      <td className={`${cell} min-w-[200px]`}>
                        <ThemedSelect
                          value={pickerValue(driver)}
                          onChange={(e) => pickDriver(String(e.target.value || ''))}
                          isClearable
                          className={selectCls}
                          aria-label={`${label} driver`}
                        >
                          <option value="">{defaultLabel(driver)}</option>
                          <option value={ALLOCATED}>Allocated driver (default)</option>
                          <option value={NO_DRIVER}>No driver</option>
                          {optionsFor(r).map((d) => (
                            <option key={d.id} value={d.id}>
                              {driverOptionLabel(d)}
                            </option>
                          ))}
                        </ThemedSelect>
                        <DriverSourceTag driver={driver} />
                      </td>
                      <td className={`${cell} min-w-[210px]`}>
                        <ThemedSelect
                          value={r.route_id}
                          onChange={(e) => set({ route_id: String(e.target.value) })}
                          className={selectCls}
                          aria-label={`${label} route`}
                        >
                          <option value="">Route...</option>
                          {routes.map((rt) => (
                            <option key={rt.id} value={rt.id}>
                              {routeLabel(rt)}
                            </option>
                          ))}
                        </ThemedSelect>
                      </td>
                      <td className={`${cell} min-w-[220px]`}>
                        <div className="grid grid-cols-[1fr_1fr_64px] gap-1">
                          <input
                            type="text"
                            value={r.material}
                            onChange={(e) => set({ material: e.target.value })}
                            placeholder="Material"
                            aria-label={`${label} material`}
                            className={input}
                          />
                          <input
                            type="text"
                            value={r.order_ref}
                            onChange={(e) => set({ order_ref: e.target.value })}
                            placeholder="Order"
                            aria-label={`${label} order`}
                            className={input}
                          />
                          <input
                            type="number"
                            min="0"
                            step="0.1"
                            value={r.planned_tonnes}
                            onChange={(e) => set({ planned_tonnes: e.target.value })}
                            placeholder="t"
                            aria-label={`${label} tonnes`}
                            className={input}
                          />
                        </div>
                      </td>
                      <td className={`${cell} min-w-[170px]`}>
                        <label htmlFor={`plan-start-${r.key}`} className="sr-only">
                          {label} planned start
                        </label>
                        <DateTimeField
                          id={`plan-start-${r.key}`}
                          value={r.planned_start}
                          onChange={(d) => set({ planned_start: d })}
                          placeholder="Start..."
                          // Phone sheet: Now, +30 min, +1 h, +2 h, Tomorrow 06:00 (ST-00264).
                          presets={PLANNING_TIME_PRESETS}
                        />
                      </td>
                      <td className={`${cell} min-w-[150px]`}>
                        <input
                          type="number"
                          min="0"
                          value={r.override_l}
                          onChange={(e) => set({ override_l: e.target.value })}
                          placeholder="Auto"
                          aria-label={`${label} fuel budget override (L)`}
                          title="Empty = calculated from the fuel norms for the route"
                          className={input}
                        />
                        {r.override_l !== '' && (
                          <input
                            type="text"
                            value={r.override_reason}
                            onChange={(e) => set({ override_reason: e.target.value })}
                            placeholder="Reason (required)"
                            aria-label={`${label} budget override reason`}
                            className={`${input} mt-1`}
                          />
                        )}
                      </td>
                      <td className={`${cell} min-w-[120px]`}>
                        <ChannelCell driver={driver} label={label} />
                      </td>
                    </>
                  )}
                  <td className={cell}>
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${pill.className}`}
                      data-status={v.status}
                    >
                      {pill.label}
                      {v.status !== 'ready' && ` · ${v.errors.length + v.warnings.length}`}
                    </span>
                  </td>
                  <td className={`${cell} text-right`}>
                    {!r.saved && (
                      <button
                        type="button"
                        onClick={() => onRemove(r.key)}
                        aria-label={`Remove ${label}`}
                        className="p-2 text-slate-400 hover:text-red-600"
                      >
                        <FaTrash size={12} />
                      </button>
                    )}
                  </td>
                </tr>
                {(v.errors.length > 0 || v.warnings.length > 0 || v.notes.length > 0) && (
                  <tr aria-label={`${label} validation`}>
                    <td />
                    <td colSpan={10} className="px-2 pb-2 -mt-1">
                      <ul className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs">
                        {v.errors.map((m) => (
                          <li key={`e-${m}`} className="text-red-700">
                            ✖ {m}
                          </li>
                        ))}
                        {v.warnings.map((m) => (
                          <li key={`w-${m}`} className="text-amber-800">
                            ⚠ {m}
                          </li>
                        ))}
                        {v.notes.map((m) => (
                          <li key={`n-${m}`} className="text-slate-500">
                            ℹ {m}
                          </li>
                        ))}
                      </ul>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
